import { setStoreValue, getStoreValue } from "@bd-stores";
import { ThingIt } from "@thing-it/client-api";

/**
 * Class ThingIt Polling - Retrieve Thing-It data, this class is special utility
 */
export default class ThingItPolling {
  constructor(config, callback = () => {}, data = {}, interval = 30) {
    this.callback = callback;
    this.data = data;
    this.interval = interval * 1000;
    this.throttlingInterval = this.interval;
    this.pollId = null;
    this.floors = [];
    this.thingItInstance = null;
    this.clientOpt = config;
    this.buildingId = getStoreValue("defaultBuildingId");
  }

  async initThingItApi() {
    this.thingItInstance = new ThingIt(this.clientOpt);
    await this.thingItInstance.retrieveToken().then((result) => {
      this.thingItInstance.setToken(result);
    });

    return this.thingItInstance.isApiReady();
  }

  initPolling = async () => {
    // throttling Thing-It API requests
    window.addEventListener("message", (event) => {
      if (event.data) {
        if (event.data.type === "viewActivated") {
          this.throttlingInterval = this.interval;
        } else if (event.data.type === "viewDeactivated") {
          this.throttlingInterval = this.interval * 4;
        }
      }
    });

    await this.initThingItApi().then(
      () => {
        this.getFloorInfo(this.buildingId);
      },
      () => {
        alert("failed to fetch data : get floor info");
      }
    );

    this.start();
    setInterval(this.refreshAccessToken, 505000);
  };

  start() {
    window.Promise.all([
      /* this.getBookedSpace(), */
      this.getSpaceOccupancy(),
      this.getDeskOccupancy(),
    ])
      .then((results) => {
        if (!results || !results.length || !results[0]) {
          return;
        }

        /* const bookedData = results[0].data; */
        const spaceData = results[0];
        const deskData = results[1];

        /* this.manageBookedSpaceData(bookedData); */
        this.manageSpaceOccupancyData(spaceData);
        this.manageDeskOccupancyData(deskData);
        this.callback([spaceData, deskData]);
      })
      .catch((error) => {
        console.error(error);
        this.refreshAccessToken();
      })
      .finally(() => {});

    this.pollId = setTimeout(() => this.start(), this.throttlingInterval);
  }

  stop() {
    if (this.pollId) clearTimeout(this.pollId);
  }

  async getFloorInfo(buildingId) {
    await this.thingItInstance
      .listFloors(this.clientOpt.mesh, buildingId)
      .then((result) => {
        const sortedData = result.items.sort((a, b) =>
          a.floorName > b.floorName ? 1 : b.floorName > a.floorName ? -1 : 0
        );

        this.floors = sortedData.map((floorInfo) => {
          return {
            name: floorInfo.floorName,
            floorId: floorInfo.floorId,
            spaceIds: {}, // collects IDs of all bookable and/or occupiable spaces
            nSpaces: 0,
            nBookableSpaces: NaN,
            bookedSpaceIds: {}, // collects IDs of all currently booked spaces
            nBookedSpaces: NaN,
            nOccupiableSpaces: NaN,
            occupiedSpaceIds: {}, // collects IDs of all currently occupied spaces
            nOccupiedSpaces: NaN,
            nDesks: 0,
            nOccupiableDesks: NaN,
            nOccupiedDesks: NaN,
          };
        });

        setStoreValue("floors", this.floors);
      });
  }

  getSpaceOccupancy = () => {
    return this.thingItInstance.getCurrentSpaceOccupancy(
      this.clientOpt.mesh,
      this.buildingId
    );
  };

  getDeskOccupancy = () => {
    return this.thingItInstance.getCurrentDeskOccupancy(
      this.clientOpt.mesh,
      this.buildingId
    );
  };

  getBookedSpace = () => {
    return this.thingItInstance.retrieveCurrentlyActiveBookings(
      this.clientOpt.mesh,
      this.buildingId
    );
  };

  /**
   * @property {Function} manageSpaceOccupancyData - Manage space (meeting room) occupancy data
   * @param {object} data - Space (meeting room) occupancy data
   * @returns {void}
   */
  manageSpaceOccupancyData = (data) => {
    const floorInfos = this.floors || [];

    floorInfos.forEach((floorInfo) => {
      floorInfo.nOccupiableSpaces = 0;
      floorInfo.nOccupiedSpaces = 0;
      floorInfo.spaceIds = {};
      floorInfo.occupiedSpaceIds = {};
    });

    const buildingId = getStoreValue("defaultBuildingId");
    let spaceOccupancyData = {};
    let currentBuildingMeetingRoom = (spaceOccupancyData[buildingId] = []);

    data.items.forEach(({ tags, values }) => {
      const floorInfo = floorInfos.find((i) => i.floorId === tags.floorId);
      if (!floorInfo) {
        return;
      }
      const spaceId = tags.spaceId;
      floorInfo.spaceIds[spaceId] = true;
      floorInfo.nSpaces = Object.keys(floorInfo.spaceIds).length;
      floorInfo.nOccupiableSpaces += 1;
      if (0 !== values[0][1]) {
        floorInfo.occupiedSpaceIds[spaceId] = true;
        floorInfo.nOccupiedSpaces = Object.keys(
          floorInfo.occupiedSpaceIds
        ).length;
      }

      // eslint-disable-next-line no-undef
      const unavailableSpaces = new Set([
        ...Object.keys(floorInfo.bookedSpaceIds),
        ...Object.keys(floorInfo.occupiedSpaceIds),
      ]);
      floorInfo.nAvailableSpaces = floorInfo.nSpaces - unavailableSpaces.size;
      floorInfo.nUnavailableSpaces = unavailableSpaces.size;

      const index = floorInfos.findIndex((p) => p.floorId == floorInfo.floorId);

      currentBuildingMeetingRoom[index] = {
        total: floorInfo.nSpaces,
        available: floorInfo.nAvailableSpaces,
      };
    });

    setStoreValue("meetingRoomAvailability", spaceOccupancyData);
  };

  /**
   * @property {Function} manageDeskOccupancyData - Manage desk occupancy data
   * @param {object} data - Desk occupancy data
   * @returns {void}
   */
  manageDeskOccupancyData = (data) => {
    const floorInfos = this.floors || [];

    floorInfos.forEach((floorInfo) => {
      floorInfo.nOccupiableDesks = 0;
      floorInfo.nOccupiedDesks = 0;
    });

    const buildingId = getStoreValue("defaultBuildingId");
    let deskOccupancyData = {};
    let currentBuildingDesk = (deskOccupancyData[buildingId] = []);

    data.items.forEach(({ tags, values }) => {
      const floorInfo = floorInfos.find((i) => i.floorId === tags.floorId);
      if (!floorInfo) {
        return;
      }
      floorInfo.nOccupiableDesks += 1;
      if (0 !== values[0][1]) {
        floorInfo.nOccupiedDesks += 1;
      }

      const index = floorInfos.findIndex((p) => p.floorId == floorInfo.floorId);

      currentBuildingDesk[index] = {
        total: floorInfo.nOccupiableDesks,
        available: floorInfo.nOccupiableDesks - floorInfo.nOccupiedDesks,
      };
    });

    setStoreValue("workStationAvailability", deskOccupancyData);
  };

  /**
   * @property {Function} manageBookedSpaceData - Manage booked space data
   * @param {object} data - Booked space data
   * @returns {void}
   */
  manageBookedSpaceData = (data) => {
    const floorInfos = this.floors || [];

    floorInfos.forEach((floorInfo) => {
      floorInfo.nBookableSpaces = 0;
      floorInfo.nBookedSpaces = 0;
      floorInfo.bookedSpaceIds = {};
    });

    const tsNow = Date.now();
    data.forEach((calendar) => {
      let isCurrentlyBooked = false;
      if (Array.isArray(calendar.bookings)) {
        isCurrentlyBooked = calendar.bookings.some(
          (booking) =>
            new Date(booking.from).valueOf() <= tsNow &&
            new Date(booking.to).valueOf() >= tsNow
        );
      }

      const floorInfo = floorInfos.find((i) => i.floorId === calendar.floor);
      if (floorInfo) {
        if (calendar.assetClass === "Space") {
          const [, , , , spaceId] = calendar.assetId.split("/");
          floorInfo.spaceIds[spaceId] = true;
          floorInfo.nSpaces = Object.keys(floorInfo.spaceIds).length;
          floorInfo.nBookableSpaces += 1;
          if (isCurrentlyBooked) {
            floorInfo.bookedSpaceIds[spaceId] = true;
            floorInfo.nBookedSpaces = Object.keys(
              floorInfo.bookedSpaceIds
            ).length;
          }

          // eslint-disable-next-line no-undef
          const unavailableSpaces = new Set([
            ...Object.keys(floorInfo.bookedSpaceIds),
            ...Object.keys(floorInfo.occupiedSpaceIds),
          ]);
          floorInfo.nAvailableSpaces =
            floorInfo.nSpaces - unavailableSpaces.size;
          floorInfo.nUnavailableSpaces = unavailableSpaces.size;
        }
      }
    });
  };

  refreshAccessToken = async (callback = () => {}) => {
    await this.thingItInstance.getUpdatedToken().then((tokenInfo) => {
      this.thingItInstance.setToken(tokenInfo);
      callback();
    });
  };

  /**
   * @property {Function} restart - Restart polling
   * @returns {void}
   */
  restart() {
    this.stop();
    this.start();
  }
}
